"use client";

import { Suspense, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { auroraLineAccentClass } from "@/lib/catalog/line-family";
import { ConfiguratorProvider, useConfigurator } from "@/store/configurator";
import { useConfiguratorUrlSync } from "@/hooks/useConfiguratorUrlSync";
import { InstrumentStep }   from "./steps/InstrumentStep";
import { StringCountStep }  from "./steps/StringCountStep";
import { LineStep }         from "./steps/LineStep";
import { GaugeStep }        from "./steps/GaugeStep";
import { ColorStep }        from "./steps/ColorStep";
import { ReviewStep }       from "./steps/ReviewStep";
import type { ConfiguratorReferenceData, ConfiguratorState } from "@/types/configurator";

const STEPS = [
  { id: 1, label: "Instrument" },
  { id: 2, label: "Strings" },
  { id: 3, label: "Line" },
  { id: 4, label: "Gauges" },
  { id: 5, label: "Color" },
  { id: 6, label: "Review" },
];

function StepIndicator({
  currentStep,
  onStepClick,
}: {
  currentStep: number;
  onStepClick: (step: number) => void;
}) {
  const { state } = useConfigurator();

  function isStepAccessible(step: number): boolean {
    if (step === 1) return true;
    if (step === 2) return !!state.instrumentId;
    if (step === 3) return !!state.instrumentId && !!state.stringCountId;
    if (step === 4) return !!state.lineId && !!state.stringTypeId;
    if (step === 5) return state.strings.length > 0;
    if (step === 6) return !!state.colorId;
    return false;
  }

  return (
    <div className="flex items-center justify-center px-4 overflow-x-auto">
      {STEPS.map((step, i) => {
        const done = step.id < currentStep;
        const active = step.id === currentStep;
        const accessible = isStepAccessible(step.id);

        return (
          <div key={step.id} className="flex items-center">
            <button
              type="button"
              onClick={() => accessible && onStepClick(step.id)}
              disabled={!accessible}
              aria-current={active ? "step" : undefined}
              aria-label={
                accessible
                  ? `${active ? "Current step" : done ? "Return to" : "Go to"} ${step.label}`
                  : `${step.label} step locked until previous selections are complete`
              }
              className={cn(
                "flex flex-col items-center gap-1 group transition-all focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#E8C547] rounded-sm",
                accessible ? "cursor-pointer" : "cursor-default opacity-40"
              )}
            >
              <div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all",
                  done   && "bg-[#40C070] text-white",
                  active && "bg-[#E8C547] text-[#0A0A0A] shadow-lg shadow-[#E8C54730]",
                  !done && !active && "border text-[#8A8A8A]"
                )}
                style={
                  !done && !active
                    ? { borderColor: "#2A2A2A", backgroundColor: "#141414" }
                    : {}
                }
              >
                {done ? <Check size={14} /> : step.id}
              </div>
              <span
                className={cn(
                  "text-[10px] font-medium uppercase tracking-wider hidden sm:block",
                  active   && "text-[#E8C547]",
                  done     && "text-[#40C070]",
                  !done && !active && "text-[#8A8A8A]"
                )}
              >
                {step.label}
              </span>
            </button>

            {i < STEPS.length - 1 && (
              <div
                // Narrow enough that six steps plus five connectors fit inside
                // a 320px viewport; the step row used to overflow it by 8px.
                className="w-3 sm:w-10 h-px mx-0.5 sm:mx-2 flex-shrink-0"
                style={{
                  backgroundColor: step.id < currentStep ? "#40C07060" : "#2A2A2A",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Header copy; the Shopify theme replaces the quote wording, which only the Next.js checkout uses. */
export interface ConfiguratorCopy {
  intro: string;
  nonStandard: string;
}

const DEFAULT_COPY: ConfiguratorCopy = {
  intro: "Choose the instrument, string count, every gauge, and the color of each string. Purchase requires a verified manufacturing specification and current quote.",
  nonStandard: "This string count requires confirmed manufacturing capability. Contact us if a verified quote is unavailable.",
};

/** The furthest step a restored build has everything for. */
function furthestStep(state: ConfiguratorState): number {
  if (state.colorId && state.strings.length > 0) return 6;
  if (state.strings.length > 0) return 5;
  if (state.lineId && state.stringTypeId) return 4;
  if (state.stringCountId) return 3;
  return state.instrumentId ? 2 : 1;
}

function ConfiguratorContent({ copy }: { copy: ConfiguratorCopy }) {
  const { state, reset } = useConfigurator();
  // A saved build (initialState) or a shared / "build again" link used to open
  // on step 1 with every choice already made. Open at the furthest step instead.
  const [currentStep, setCurrentStep] = useState(() => furthestStep(state));
  const router = useRouter();
  useConfiguratorUrlSync({ onRestored: setCurrentStep });

  // Long steps leave the viewport mid-scroll; start each step at the top.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [currentStep]);

  function next() {
    setCurrentStep((s) => Math.min(s + 1, STEPS.length));
  }
  function back() {
    setCurrentStep((s) => Math.max(s - 1, 1));
  }

  function resetBuild() {
    reset();
    setCurrentStep(1);
    // Current path, not "/configure": the same shell also runs inside the Shopify theme.
    router.replace(window.location.pathname, { scroll: false });
  }

  const stepProps = { onNext: next, onBack: back, onReset: resetBuild };
  // Review needs to send the customer back to a specific step, not just one
  // step back, so every summary group can offer a direct way to edit it.
  const editStep = (step: number) => setCurrentStep(step);

  return (
    // The working area stays on the store's neutral ground. It used to carry
    // the selected line's full-bleed `bg-*` treatment — a large saturated
    // gradient behind the gauge and colour controls, which competed with the
    // fretboard and with the selected-string highlight. Line identity is now a
    // small accented badge below the step indicator.
    <div style={{ backgroundColor: "#0A0A0A", minHeight: "80vh" }}>
      {/* Header */}
      <div
        className="border-b py-8"
        style={{
          borderColor: "#2A2A2A",
          background: "linear-gradient(to bottom, #0F0A00 0%, #0A0A0A 100%)",
        }}
      >
        <div className="container-main text-center mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] mb-2" style={{ color: "#E8C547" }}>
            Custom String Configurator
          </p>
          <h1
            className="text-3xl sm:text-4xl font-black"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Build Your Set
          </h1>
          <p className="mt-2 text-sm" style={{ color: "#9A9A9A" }}>
            {copy.intro}
          </p>
        </div>
        <StepIndicator currentStep={currentStep} onStepClick={setCurrentStep} />
        {/* Mobile: dots have no labels, so name the current step */}
        <p className="mt-3 text-center text-xs font-medium sm:hidden" style={{ color: "#9A9A9A" }}>
          Step {currentStep} of {STEPS.length} · {STEPS[currentStep - 1].label}
        </p>
        <BuildContextBadge />
      </div>

      {/* Non-standard notice */}
      {state.isNonStandard && (
        <div
          className="border-b px-4 py-3 text-sm text-center"
          style={{
            backgroundColor: "#E8C54710",
            borderColor: "#E8C54730",
            color: "#E8C547",
          }}
        >
          {copy.nonStandard}
        </div>
      )}

      {/* Step content — keyed so each step animates in */}
      <div key={currentStep} className="container-main py-10 max-w-3xl animate-step-enter">
        {currentStep === 1 && <InstrumentStep  {...stepProps} />}
        {currentStep === 2 && <StringCountStep {...stepProps} />}
        {currentStep === 3 && <LineStep        {...stepProps} />}
        {currentStep === 4 && <GaugeStep       {...stepProps} />}
        {currentStep === 5 && <ColorStep       {...stepProps} />}
        {currentStep === 6 && <ReviewStep      {...stepProps} onEditStep={editStep} />}
      </div>
    </div>
  );
}

/**
 * Which line (and construction) the customer is building — the identity the
 * full-bleed background used to carry, at a size that does not compete with
 * the controls. Hidden until a line is chosen.
 */
function BuildContextBadge() {
  const { state, referenceData } = useConfigurator();
  const line = referenceData.lines.find((entry) => entry.slug === state.lineId);
  if (!line) return null;
  const stringType = line.stringTypes.find((type) => type.slug === state.stringTypeId);

  return (
    <div className="mt-4 flex justify-center">
      <p
        className={cn("inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs", auroraLineAccentClass(line.slug))}
        style={{ borderColor: "#2A2A2A", backgroundColor: "#141414", color: "#C0C0C0" }}
      >
        <span
          aria-hidden
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: "var(--line-accent)" }}
        />
        <span className="font-semibold" style={{ color: "var(--line-accent)" }}>{line.name}</span>
        {stringType ? <span style={{ color: "#8A8A8A" }}>· {stringType.name}</span> : null}
      </p>
    </div>
  );
}

export function ConfiguratorShell({
  referenceData,
  initialState,
  copy = DEFAULT_COPY,
}: {
  referenceData: ConfiguratorReferenceData;
  initialState?: ConfiguratorState;
  copy?: ConfiguratorCopy;
}) {
  return (
    <ConfiguratorProvider referenceData={referenceData} initialState={initialState}>
      <Suspense>
        <ConfiguratorContent copy={copy} />
      </Suspense>
    </ConfiguratorProvider>
  );
}
